import { NodeIndexOutlined } from "@ant-design/icons"
import { Alert, Button, Divider, Flex, Switch, Typography } from "antd"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { RoutesPaths } from "../../../../../constants/routes.ts"
import { useAppSelector } from "../../../../../hooks/use-app-selector.ts"
import { unwrapEnvelope } from "../../../../../utils/api/unwrap-envelope.ts"
import { showResponseMessage } from "../../../../../utils/handlers/show-response-message.ts"
import {
  type StatusAutomationPreview,
  type StatusAutomationRuleDefinition,
  type StatusAutomationRuleSetting,
  type StatusAutomationSettings,
  getStatusAutomationSettings,
  previewStatusAutomation,
  updateStatusAutomationSettings
} from "../../../api/status-automation-settings.services.ts"
import { SettingsCard } from "../../common/SettingsCard.tsx"
import {
  type PreviewState,
  StatusAutomationPreviewDrawer
} from "./StatusAutomationPreviewDrawer.tsx"
import { StatusAutomationRuleRow } from "./StatusAutomationRuleRow.tsx"
import {
  type StatusAutomationDraft,
  WON_TO_PROJECT_RULE,
  buildPatch,
  draftFrom,
  isDirty,
  ruleErrorsFrom,
  withRecommendedRules
} from "./status-automation-form.ts"

const { Text, Title } = Typography

const S = "settingsConfiguration.statusAutomation"

export function StatusAutomationSettingsCard() {
  const { t } = useTranslation()
  const canEdit = !!useAppSelector(
    (state) =>
      state?.auth?.userInfo?.permissions?.codes?.settings?.company
        ?.settings_company_edit
  )

  const [catalog, setCatalog] = useState<StatusAutomationRuleDefinition[]>([])
  const [saved, setSaved] = useState<StatusAutomationDraft | null>(null)
  const [draft, setDraft] = useState<StatusAutomationDraft | null>(null)
  // Locked until the stored settings arrive: a blank form saved over a failed
  // load would overwrite every rule the company chose.
  const [loadState, setLoadState] = useState<"loading" | "loaded" | "error">(
    "loading"
  )
  const [loadAttempt, setLoadAttempt] = useState(0)
  const [saving, setSaving] = useState(false)
  const [ruleErrors, setRuleErrors] = useState<Record<string, string[]>>({})
  const [preview, setPreview] = useState<PreviewState>({ status: "closed" })
  const previewRequest = useRef(0)

  useEffect(() => {
    let cancelled = false
    setLoadState("loading")

    void getStatusAutomationSettings().then((response) => {
      if (cancelled) return
      const settings =
        response?.ok && response.data
          ? unwrapEnvelope<StatusAutomationSettings | null>(response.data)
          : null
      if (settings && Array.isArray(settings.rule_catalog)) {
        const loaded = draftFrom(settings)
        setCatalog(settings.rule_catalog)
        setSaved(loaded)
        setDraft(loaded)
        setLoadState("loaded")
      } else {
        setLoadState("error")
      }
    })

    return () => {
      cancelled = true
    }
  }, [loadAttempt])

  const editable = canEdit && loadState === "loaded" && !saving
  const dirty = isDirty(draft, saved)

  const setRule = (key: string, setting: StatusAutomationRuleSetting) =>
    setDraft((current) =>
      current
        ? { ...current, rules: { ...current.rules, [key]: setting } }
        : current
    )

  const handleSave = async () => {
    if (!draft || !saved) return
    setSaving(true)
    setRuleErrors({})
    try {
      const response = await updateStatusAutomationSettings(
        buildPatch(draft, saved)
      )
      // A rejection is already surfaced by the shared response handler.
      if (response?.ok && response.data) {
        showResponseMessage(response, t(`${S}.saved`))
        setLoadAttempt((n) => n + 1)
      } else {
        setRuleErrors(ruleErrorsFrom(response?.data))
      }
    } finally {
      setSaving(false)
    }
  }

  const handlePreview = async () => {
    if (!draft) return
    const request = ++previewRequest.current
    setPreview({ status: "loading" })
    const response = await previewStatusAutomation({ rules: draft.rules })
    if (request !== previewRequest.current) return
    const result =
      response?.ok && response.data
        ? unwrapEnvelope<StatusAutomationPreview | null>(response.data)
        : null
    setPreview(
      result && Array.isArray(result.moves)
        ? { status: "loaded", preview: result }
        : { status: "error" }
    )
  }

  const closePreview = () => {
    previewRequest.current += 1
    setPreview({ status: "closed" })
  }

  const rowsFor = (entity: "lead" | "project") =>
    catalog
      .filter(
        (definition) =>
          definition.entity === entity && definition.key !== WON_TO_PROJECT_RULE
      )
      .map((definition) =>
        draft?.rules[definition.key] ? (
          <StatusAutomationRuleRow
            key={definition.key}
            definition={definition}
            setting={draft.rules[definition.key]}
            disabled={!editable}
            errors={ruleErrors[definition.key]}
            onChange={(setting) => setRule(definition.key, setting)}
          />
        ) : null
      )

  const wonToProject = catalog.find(
    (definition) => definition.key === WON_TO_PROJECT_RULE
  )

  return (
    <SettingsCard
      iconWrapperClassName="bg-blue-100"
      icon={<NodeIndexOutlined className="text-blue-600" />}
      title={t(`${S}.title`)}
      subtitle={t(`${S}.subtitle`)}
    >
      {loadState === "error" && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message={t(`${S}.loadError`)}
          action={
            <Button size="small" onClick={() => setLoadAttempt((n) => n + 1)}>
              {t(`${S}.retry`)}
            </Button>
          }
        />
      )}
      {!canEdit && (
        <Alert type="info" showIcon className="mb-4" message={t(`${S}.readOnly`)} />
      )}

      <Flex vertical gap={4}>
        <Flex align="center" gap={12}>
          <Switch
            id="status-automation-enabled"
            disabled={!editable}
            checked={draft?.enabled ?? false}
            onChange={(enabled) =>
              setDraft((current) => (current ? { ...current, enabled } : current))
            }
          />
          <label htmlFor="status-automation-enabled">
            <Text strong>{t(`${S}.enabled`)}</Text>
          </label>
        </Flex>
        <Text type="secondary">
          {t(`${S}.enabledHint`)}{" "}
          <Link to={RoutesPaths.settings.automation}>{t(`${S}.openAutomations`)}</Link>
        </Text>
      </Flex>

      <Divider />
      <Title level={5}>{t(`${S}.leadRules`)}</Title>
      <div>{rowsFor("lead")}</div>

      <Divider />
      <Title level={5}>{t(`${S}.projectRules`)}</Title>
      <div>{rowsFor("project")}</div>

      {wonToProject && draft?.rules[WON_TO_PROJECT_RULE] ? (
        <>
          <Divider />
          <Title level={5}>{t(`${S}.wonToProject`)}</Title>
          <StatusAutomationRuleRow
            definition={wonToProject}
            setting={draft.rules[WON_TO_PROJECT_RULE]}
            disabled={!editable}
            note={t(`${S}.wonToProjectNote`)}
            errors={ruleErrors[WON_TO_PROJECT_RULE]}
            onChange={(setting) => setRule(WON_TO_PROJECT_RULE, setting)}
          />
        </>
      ) : null}

      <Divider />
      <Title level={5}>{t(`${S}.notifications`)}</Title>
      <Flex align="center" gap={12}>
        <Switch
          id="status-automation-notify-owner"
          disabled={!editable}
          checked={draft?.notify_owner ?? false}
          onChange={(notify_owner) =>
            setDraft((current) =>
              current ? { ...current, notify_owner } : current
            )
          }
        />
        <label htmlFor="status-automation-notify-owner">
          {t(`${S}.notifyOwner`)}
        </label>
      </Flex>

      <Divider />
      <Flex justify="end" gap={8} wrap>
        {canEdit ? (
          <Button
            disabled={!editable}
            onClick={() =>
              setDraft((current) =>
                current ? withRecommendedRules(current, catalog) : current
              )
            }
          >
            {t(`${S}.useRecommended`)}
          </Button>
        ) : null}
        <Button
          disabled={loadState !== "loaded"}
          onClick={() => void handlePreview()}
        >
          {t(`${S}.previewImpact`)}
        </Button>
        {canEdit ? (
          <Button
            type="primary"
            loading={saving}
            disabled={!editable || !dirty}
            onClick={() => void handleSave()}
          >
            {t(`${S}.save`)}
          </Button>
        ) : null}
      </Flex>

      <StatusAutomationPreviewDrawer state={preview} onClose={closePreview} />
    </SettingsCard>
  )
}
